/**
 * Any deal-field value the dossier prints, editable in place once unlocked.
 *
 * Owner request (21 Oct): "When the user unlocks the property dossier … it
 * only unlocks certain cells in the page and throughout the dossier. However
 * if the user goes to the Audit tab and finds the field, they can edit it
 * there. … Make it so that any value or populated data point in the dossier
 * is able to be edited once the unlock function is enabled."
 *
 * The Audit tab proves every catalogue field can be written through one path:
 * the dossier's draft (`setField`), its server-side derivation preview, and its
 * one Save with the change reason. `EditableSlot` already used that path, but
 * only where a page had been rebuilt around its tile / row / stack boxes; a
 * value printed inside a table cell, a `dd`, a header line or a chart caption
 * stayed text. This is the cheap way for any of those to take part: wrap the
 * value the page already prints, name the field, and nothing else changes.
 *
 *   locked      exactly what the page drew before (`children`, or `display`
 *               with its `marks`) — no wrapper, no extra node
 *   input       the same catalogue input the header strip and EditableSlot
 *               use, sized to the text it replaces, on the same line, with
 *               the value's state and attribution chips still beside it
 *   composite   a value made of several fields ("Year Built / Reno" is two,
 *               an address four) is edited as those fields: two side by side,
 *               more in a small panel opened from the value
 *   computed    a derived figure — the engine's (lineage Derived), the page's
 *               own arithmetic, or the calculation service's while the field
 *               is empty — stays read-only and opens its inputs instead (R1)
 *   readonly    an identity field, or a value no column holds, reads as it
 *               does locked, with the reason on hover
 *
 * There is no second save path. Every input here calls `setField`, and the
 * dossier's existing Save writes the draft (`buildDossierPatch`) exactly as it
 * writes an edit made on the Audit tab.
 */
import { Fragment, useState, type ReactNode } from 'react'

import { editableValue } from '@/services/dealFile'
import { useDossierEdit, type DossierEdit } from './editContext'
import {
  isSystemField,
  isWritableField,
  slotKindOf,
  SYSTEM_FIELD_REASON,
  unwritableReason,
  type EditTarget,
  type SlotKind,
} from './editableFields'
import { activateOnKey, ComputedTrigger, EditPopover, InputEditorList, outputFormulaOf } from './computedInputs'
import { renderCatalogueInput, SlotValueInput } from './ui'

export type ValueEditMode = 'locked' | 'input' | 'composite' | 'computed' | 'readonly'

/** A value that holds something. */
function present(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

/**
 * How one displayed value behaves in the dossier as it stands. Exported so a
 * caller can lay out what it draws around the value by the mode it is in.
 */
export function valueEditMode(
  edit: DossierEdit,
  name: string,
  opts: { fields?: EditTarget[]; computed?: string | null; popover?: boolean } = {},
): { mode: ValueEditMode; formula: string | null; targets: EditTarget[] } {
  const targets = opts.fields ?? [{ name, label: name }]
  if (!edit.unlocked) return { mode: 'locked', formula: null, targets }
  if (targets.length === 0) return { mode: 'readonly', formula: null, targets }
  const own = targets.length === 1 ? outputFormulaOf(edit, targets[0].name) : null
  const formula = opts.computed !== undefined && opts.computed !== null ? opts.computed : own
  if (formula !== null) return { mode: 'computed', formula, targets }
  const writable = targets.filter(
    (target) =>
      !isSystemField(target.name)
      && isWritableField(target.name, edit.defs)
      && outputFormulaOf(edit, target.name) === null,
  )
  if (writable.length === 0) return { mode: 'readonly', formula: null, targets }
  if (opts.popover || targets.length > 2) return { mode: 'composite', formula: null, targets }
  return { mode: 'input', formula: null, targets }
}

/** True when the value shown stands in for a field that holds nothing: the
 *  calculation service's figure, or the page's fallback, shown while the deal
 *  states none. That figure is computed, and an input over it would blank it. */
export function showsStandIn(edit: DossierEdit, name: string, display: ReactNode): boolean {
  if (present(edit.effectiveValue(name))) return false
  return typeof display === 'string' && display.trim() !== '' && display.trim() !== '—'
}

/**
 * A wrapper that is laid out as the content it holds: inline, and spaced as
 * its parent spaces its own children (`gap: inherit`), so a value and its
 * chips inside a flex row keep the gap the row gives them.
 *
 * Top-aligned in a line of text: an input is exactly one line tall
 * (`height: 1lh`), and sat on the baseline it lifted the header's facts line
 * from 15px to 16px. Measured on Washington Square at 1600.
 */
function Wrap({ children, ...rest }: { children: ReactNode; title?: string; 'data-edit-state'?: string }) {
  return (
    <span className="inline-flex min-w-0 max-w-full items-center align-top [gap:inherit]" {...rest}>
      {children}
    </span>
  )
}

/**
 * Keeps a trigger inline inside the flex wrapper. A flex item is laid out as a
 * block, so the composite trigger's 1px hairline counted toward the height of
 * the line it sat in and the header's address line grew by 2px when the
 * dossier unlocked; inside a plain span it is an inline box again, and an
 * inline box's border never changes the line's height.
 */
function InlineSlot({ children }: { children: ReactNode }) {
  return <span className="min-w-0">{children}</span>
}

/**
 * One input, exactly as wide as the text it stands in for.
 *
 * The box is laid over an invisible copy of the text it replaces (the copy
 * gives the wrapper its width; the input fills it), as the compact metric bar
 * does: a width counted in `ch` was the wrong size in both directions — 43px too wide
 * for the deal name in the header's display face, and a digit short for
 * "$132,000,000" in JetBrains Mono, where an input resolves `ch` narrower than
 * the text it holds. The copy is the longer of what read mode printed and
 * what the box holds ("$132M" becomes "$132,000,000"), so the full figure is
 * always visible, and it follows the value as it is typed.
 */
function FieldBox({ name, label, kind, display }: { name: string; label: string; kind?: SlotKind; display: string }) {
  const edit = useDossierEdit()
  const def = edit.defs.find((d) => d.name === name)
  const slotKind = kind ?? slotKindOf(def)
  const held = editableValue(def, edit.effectiveValue(name))
  const date = slotKind === 'date' || def?.kind === 'date'
  // A select draws its arrow inside its own box: room for it beside the text.
  const select = def?.kind === 'select' && (def.options?.length ?? 0) > 0
  const sizer = [display.trim(), held, date ? 'MM/DD/YYYY' : '', '———'].reduce(
    (longest, text) => (text.length > longest.length ? text : longest),
    '',
  )
  // The input is taken out of flow and laid over the copy: an <input> left in
  // flow brings its own intrinsic width (twenty characters), which widened the
  // deal name by 71px and wrapped the header's facts line onto a second row.
  //
  // A native select needs 31px beyond its text in Chromium (1px borders, 2px
  // padding and the arrow), measured at 10, 11, 12 and 24px type; the box is
  // this copy plus the 6px of its negative margins, so the copy reserves 26px.
  // The 20px it reserved before cut the selected value short: the Financing
  // Summary's Position read "Senio", the Overview's Property Type "Multifamil".
  return (
    <span
      data-edit-box={name}
      title={label}
      className="relative inline-block min-w-0 max-w-full align-top [&_.dossier-edit-input]:absolute [&_.dossier-edit-input]:left-0 [&_.dossier-edit-input]:top-0"
    >
      {/* A select's arrow and a date's calendar button sit inside the box. */}
      <span aria-hidden="true" className={`invisible whitespace-pre ${select ? 'pr-[26px]' : date ? 'pr-4' : ''}`}>
        {sizer}
      </span>
      <SlotValueInput name={name} label={label} kind={slotKind} allowStage />
    </span>
  )
}

/**
 * A value edited as several fields, unlocked: the value itself, marked with
 * the edit-mode gold hairline every input carries (it IS editable), opening
 * one input per field. Inline like the computed trigger, so the hairline sits
 * in the line's own margin and nothing moves.
 */
function CompositeTrigger({ label, targets, display }: { label: string; targets: EditTarget[]; display: ReactNode }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const names = targets.map((target) => target.name)
  const labels = Object.fromEntries(targets.map((target) => [target.name, target.label]))
  const open = (target: HTMLElement) => setAnchor((current) => (current ? null : target))
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        data-edit-composite={names[0]}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        title={`Edit ${label}: ${targets.map((target) => target.label).join(', ')}`}
        onClick={(event) => open(event.currentTarget)}
        onKeyDown={activateOnKey(open)}
        className="dossier-edit-trigger"
      >
        {display}
      </span>
      {anchor ? (
        <EditPopover anchor={anchor} title={label} onClose={() => setAnchor(null)}>
          <InputEditorList names={names} labels={labels} renderInput={renderCatalogueInput} />
        </EditPopover>
      ) : null}
    </>
  )
}

export function EditableValue({
  name,
  label,
  kind,
  display,
  marks = null,
  marksFirst = false,
  source = null,
  children,
  fields,
  popover = false,
  computed,
  inputs,
  readOnlyReason,
}: {
  /** The catalogue field the value is. */
  name: string
  /** What the value is called on this screen; the input's accessible name. */
  label: string
  /** The input's keyboard; the catalogue entry's kind when not given. */
  kind?: SlotKind
  /** The value as the page prints it. */
  display: ReactNode
  /** What the page draws beside the value (state chips, attribution). Kept
   *  in every mode, the input included: the moment a person overwrites a
   *  figure is the moment "the client confirmed this" or "the sponsor's
   *  model says so" matters most, and dropping the chips while the box stood
   *  in the value's place hid exactly that (21 Oct review). */
  marks?: ReactNode
  /** The value's own way to where it came from — the document it was read
   *  from, or how it was derived — as a compact control. Locked, the page's
   *  `children` already make the value itself that link; while the value is
   *  an input, or opens its fields, the value cannot be the link too, so this
   *  stands after the marks instead. */
  source?: ReactNode
  /** The marks come before the value (a right-aligned `dd`). */
  marksFirst?: boolean
  /** The page's own locked rendering when it is more than `display` and
   *  `marks` — a source link, a detail button. Drawn unchanged when locked. */
  children?: ReactNode
  /** The fields a composed value is edited as. */
  fields?: EditTarget[]
  /** Edit the fields in a panel opened from the value, however few there are. */
  popover?: boolean
  /** The value shown is computed (this page's arithmetic, or the calculation
   *  service's figure for an empty field); the formula, or '' when unnamed. */
  computed?: string | null
  /** The inputs of `computed`, when the page knows them better than the
   *  catalogue does. */
  inputs?: string[]
  /** Why a read-only value cannot be edited, when the page knows better. */
  readOnlyReason?: string
}) {
  const edit = useDossierEdit()
  const { mode, formula, targets } = valueEditMode(edit, name, {
    fields: fields ?? [{ name, label, kind }],
    computed,
    popover,
  })
  const locked = children ?? (
    <>
      {marksFirst ? marks : null}
      {display}
      {marksFirst ? null : marks}
    </>
  )

  switch (mode) {
    case 'locked':
      return <>{locked}</>
    case 'readonly': {
      const reason =
        readOnlyReason
        ?? (targets.length > 0 && targets.every((target) => isSystemField(target.name))
          ? SYSTEM_FIELD_REASON
          : targets.length === 0
            ? 'Not a field the deal stores.'
            : unwritableReason(name))
      return (
        <Wrap data-edit-state="readonly" title={reason}>
          {locked}
        </Wrap>
      )
    }
    case 'computed':
      return (
        <Wrap data-edit-state="computed">
          {marksFirst ? marks : null}
          <InlineSlot>
            <ComputedTrigger name={name} label={label} formula={formula} inputs={inputs} renderInput={renderCatalogueInput}>
              {display}
            </ComputedTrigger>
          </InlineSlot>
          {marksFirst ? null : marks}
        </Wrap>
      )
    case 'composite':
      return (
        <Wrap data-edit-state="composite">
          {marksFirst ? marks : null}
          <InlineSlot>
            <CompositeTrigger label={label} targets={targets} display={display} />
          </InlineSlot>
          {marksFirst ? null : marks}
          {source}
        </Wrap>
      )
    case 'input': {
      // Typed over, the value's marks would describe a figure no longer in the
      // box: a Sponsor chip beside the lender's own number misattributes it.
      // Until then they stay, which is when they matter (21 Oct review).
      const typedOver = targets.some((target) => Object.prototype.hasOwnProperty.call(edit.draft, target.name))
      const shownMarks = typedOver ? null : marks
      return (
        <Wrap data-edit-state="input">
          {marksFirst ? shownMarks : null}
          {targets.map((target, index) => {
            const writable =
              !isSystemField(target.name)
              && isWritableField(target.name, edit.defs)
              && outputFormulaOf(edit, target.name) === null
            // Two boxes on one line are two fields: a separator between them
            // and each one's unit after it, so "Amortization" reads as a type
            // and a number of months rather than two bare boxes whose
            // hairlines touched ("– ▾|–").
            const joiner = target.separator ?? '/'
            const separator =
              index > 0 && joiner ? (
                <span aria-hidden="true" className="text-stone-500">
                  {joiner}
                </span>
              ) : null
            const unit = target.unit ? (
              <span aria-hidden="true" className="text-stone-500">
                {target.unit}
              </span>
            ) : null
            if (!writable) {
              return (
                <Fragment key={target.name}>
                  {separator}
                  <span title={unwritableReason(target.name)}>{String(edit.effectiveValue(target.name) ?? '—')}</span>
                  {unit}
                </Fragment>
              )
            }
            return (
              <Fragment key={target.name}>
                {separator}
                <FieldBox
                  name={target.name}
                  label={targets.length === 1 ? label : target.label}
                  kind={target.kind ?? (targets.length === 1 ? kind : undefined)}
                  display={targets.length === 1 && typeof display === 'string' ? display : ''}
                />
                {unit}
              </Fragment>
            )
          })}
          {marksFirst ? null : shownMarks}
          {source}
        </Wrap>
      )
    }
    default: {
      const _exhaustive: never = mode
      return _exhaustive
    }
  }
}
